import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  useCreateGradingStructureMutation,
  useDeleteGradingStructureMutation,
  useGetGradingStructuresQuery,
} from '@/state/course';
import { skipToken } from '@reduxjs/toolkit/query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface CourseGradingSchemeDialogProps {
  tenant?: string;
  open: boolean;
  selectedGradingStructure:
    | {
        id: number;
        name?: string;
      }
    | string
    | number
    | null
    | undefined;
  isSelectingScheme: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectScheme: (gradingStructureId: number | null) => void;
}

interface GradingRowDraft {
  grade: string;
  lower_threshold: string;
  upper_threshold: string;
}

const DEFAULT_ROWS: GradingRowDraft[] = [{ grade: 'A', lower_threshold: '0', upper_threshold: '100' }];

const normalizeRowsWithFixedUpper = (rows: GradingRowDraft[]): GradingRowDraft[] =>
  rows.map((row, index, allRows) => {
    if (index === 0) {
      return {
        ...row,
        upper_threshold: '100',
      };
    }
    const prevLower = Number(allRows[index - 1]?.lower_threshold ?? 0);
    const nextUpper = Number.isFinite(prevLower) ? Math.max(prevLower - 1, 0) : 0;
    return {
      ...row,
      upper_threshold: String(nextUpper),
    };
  });

const CourseGradingSchemeDialog = ({
  tenant,
  open,
  selectedGradingStructure,
  isSelectingScheme,
  onOpenChange,
  onSelectScheme,
}: CourseGradingSchemeDialogProps) => {
  const [isAddMode, setIsAddMode] = useState(false);
  const [schemeName, setSchemeName] = useState('');
  const [gradingRows, setGradingRows] = useState<GradingRowDraft[]>(DEFAULT_ROWS);
  const [openSchemeIds, setOpenSchemeIds] = useState<number[]>([]);
  const [schemeToDelete, setSchemeToDelete] = useState<number | null>(null);
  const { data: gradingSchemes = [] } = useGetGradingStructuresQuery(
    tenant ? { tenant } : skipToken
  );
  const [createGradingStructure, { isLoading: isCreatingScheme }] =
    useCreateGradingStructureMutation();
  const [deleteGradingStructure, { isLoading: isDeletingScheme }] =
    useDeleteGradingStructureMutation();

  const selectedSchemeId = useMemo(() => {
    if (selectedGradingStructure === null || selectedGradingStructure === undefined) return null;
    if (typeof selectedGradingStructure === 'object' && 'id' in selectedGradingStructure) {
      return selectedGradingStructure.id;
    }
    const parsed = Number(selectedGradingStructure);
    if (Number.isFinite(parsed)) return parsed;
    const selectedName = String(selectedGradingStructure).trim().toLowerCase();
    const matched = gradingSchemes.find(
      (scheme) => scheme.grading_structure_name.trim().toLowerCase() === selectedName
    );
    return matched?.id ?? null;
  }, [selectedGradingStructure, gradingSchemes]);

  const resetAddMode = () => {
    setIsAddMode(false);
    setSchemeName('');
    setGradingRows(DEFAULT_ROWS);
  };

  const addNewRow = () => {
    const last = gradingRows[gradingRows.length - 1];
    const lastLower = Number(last?.lower_threshold ?? 0);
    if (!Number.isFinite(lastLower) || lastLower <= 0) return;
    setGradingRows((prev) =>
      normalizeRowsWithFixedUpper([
        ...prev,
        {
          grade: '',
          lower_threshold: '0',
          upper_threshold: '0',
        },
      ])
    );
  };

  const updateRow = (index: number, key: keyof GradingRowDraft, value: string) => {
    setGradingRows((prev) => {
      const next = prev.map((row, rowIndex) => {
        if (rowIndex !== index) return row;
        if (key === 'upper_threshold') return row;
        if (key === 'lower_threshold') {
          const parsed = Number(value);
          const maxLower = Number(row.upper_threshold);
          const clamped =
            Number.isFinite(parsed) && Number.isFinite(maxLower)
              ? Math.min(Math.max(parsed, 0), maxLower)
              : 0;
          return { ...row, lower_threshold: String(clamped) };
        }
        return { ...row, [key]: value };
      });
      return normalizeRowsWithFixedUpper(next);
    });
  };

  const removeRow = (index: number) => {
    setGradingRows((prev) => normalizeRowsWithFixedUpper(prev.filter((_, rowIndex) => rowIndex !== index)));
  };

  const handleCreateScheme = async () => {
    if (!tenant) return;
    const trimmedName = schemeName.trim();
    if (!trimmedName) {
      toast.error('Grading scheme name is required.');
      return;
    }
    const payloadRows = gradingRows
      .map((row) => ({
        grade: row.grade.trim(),
        lower_threshold: row.lower_threshold,
        upper_threshold: row.upper_threshold,
      }))
      .filter((row) => row.grade.length > 0);

    if (payloadRows.length === 0) {
      toast.error('Please add at least one valid grade row.');
      return;
    }
    try {
      await createGradingStructure({
        tenant,
        data: {
          grading_structure_name: trimmedName,
          grading_structure: payloadRows,
        },
      }).unwrap();
      toast.success('Grading scheme created successfully.');
      resetAddMode();
    } catch {
      toast.error('Failed to create grading scheme.');
    }
  };

  const toggleCollapsible = (schemeId: number) => {
    setOpenSchemeIds((prev) =>
      prev.includes(schemeId) ? prev.filter((id) => id !== schemeId) : [...prev, schemeId]
    );
  };

  const handleDeleteScheme = async () => {
    if (!tenant || !schemeToDelete) return;
    try {
      await deleteGradingStructure({
        tenant,
        gradingStructureId: schemeToDelete,
      }).unwrap();
      toast.success('Grading scheme deleted successfully.');
      if (selectedSchemeId === schemeToDelete) {
        onSelectScheme(null);
      }
      setSchemeToDelete(null);
    } catch {
      toast.error('Failed to delete grading scheme.');
    }
  };

  const getIsSchemeSelected = (schemeId: number) => selectedSchemeId === schemeId;
  const isAddRowDisabled = Number(gradingRows[gradingRows.length - 1]?.lower_threshold ?? 0) <= 0;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[92vh] overflow-y-auto p-0 sm:max-w-[640px]" showCloseButton={false}>
          <DialogHeader className="border-b px-5 py-4">
            <div className="flex items-center justify-between gap-3">
              <DialogTitle>{isAddMode ? 'Add grading scheme' : 'Select grading scheme'}</DialogTitle>
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-muted/70 hover:bg-muted"
                onClick={() => onOpenChange(false)}
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </button>
            </div>
          </DialogHeader>

          {isAddMode ? (
            <div className="space-y-4 p-5">
              <div className="space-y-1">
                <p className="text-sm font-medium">Grading scheme name</p>
                <Input
                  value={schemeName}
                  onChange={(event) => setSchemeName(event.target.value)}
                  placeholder="Enter scheme name"
                />
              </div>

              <div className="rounded-md border">
                <div className="grid grid-cols-[120px_minmax(0,1fr)_130px] border-b bg-muted/40 px-3 py-2 text-sm font-medium">
                  <p>Grade</p>
                  <p>Range</p>
                  <p />
                </div>
                <div className="space-y-2 p-3">
                  {gradingRows.map((row, index) => (
                    <div key={`grading-row-${index}`} className="grid grid-cols-[80px_minmax(0,1fr)_130px] items-center gap-2">
                      <Input
                        value={row.grade}
                        onChange={(event) => updateRow(index, 'grade', event.target.value)}
                        placeholder="A"
                      />
                      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                        <div className="inline-flex h-10 items-center rounded-md border bg-muted/30 px-3 text-sm">
                          {row.upper_threshold}
                        </div>
                        <span className="text-sm text-muted-foreground">to</span>
                        <Input
                          type="number"
                          value={row.lower_threshold}
                          onChange={(event) => updateRow(index, 'lower_threshold', event.target.value)}
                          placeholder="80"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        className="justify-start text-destructive hover:text-destructive"
                        onClick={() => removeRow(index)}
                        disabled={gradingRows.length <= 1}
                      >
                        <Trash2 className="h-4 w-4" />
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2">
                <Button type="button" variant="secondary" onClick={addNewRow} disabled={isAddRowDisabled}>
                  <Plus className="h-4 w-4" />
                  Add grade
                </Button>
                <div className="flex items-center gap-2">
                  <Button type="button" variant="outline" onClick={resetAddMode}>
                    Cancel
                  </Button>
                  <Button type="button" onClick={() => void handleCreateScheme()} disabled={isCreatingScheme}>
                    Save
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4 p-5">
              <div className="flex justify-end">
                <Button type="button" variant="secondary" onClick={() => setIsAddMode(true)}>
                  <Plus className="h-4 w-4" />
                  Add new grading scheme
                </Button>
              </div>

              <div className="space-y-3">
                {gradingSchemes.map((scheme) => {
                  const isOpen = openSchemeIds.includes(scheme.id);
                  const isSelected = getIsSchemeSelected(scheme.id);
                  return (
                    <Collapsible key={scheme.id} open={isOpen} onOpenChange={() => toggleCollapsible(scheme.id)}>
                      <div className="rounded-md border">
                        <div className="flex items-center justify-between gap-2 rounded-t-md bg-muted/50 px-3 py-2">
                          <CollapsibleTrigger asChild>
                            <button type="button" className="inline-flex items-center gap-2 text-left text-sm font-medium">
                              {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                              <span>{scheme.grading_structure_name}</span>
                            </button>
                          </CollapsibleTrigger>
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              disabled={isSelectingScheme}
                              onClick={() => onSelectScheme(isSelected ? null : scheme.id)}
                            >
                              {isSelected ? 'Unselect' : 'Select'}
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              onClick={() => setSchemeToDelete(scheme.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                              <span className="sr-only">Delete scheme</span>
                            </Button>
                          </div>
                        </div>

                        <CollapsibleContent>
                          <div className="p-3">
                            <div className="rounded-md border">
                              <div className="grid grid-cols-2 border-b bg-muted/30 px-3 py-2 text-sm font-medium">
                                <p>Grade</p>
                                <p>Range</p>
                              </div>
                              <div className="space-y-2 p-3 text-sm">
                                {scheme.grading_structure.map((item) => (
                                  <div key={item.id} className="grid grid-cols-2">
                                    <p>{item.grade}</p>
                                    <p>
                                      {item.upper_threshold} - {item.lower_threshold}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  );
                })}

                {gradingSchemes.length === 0 ? (
                  <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                    No grading schemes available.
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={schemeToDelete !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setSchemeToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete grading scheme?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the selected grading scheme.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingScheme}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeletingScheme}
              onClick={(event) => {
                event.preventDefault();
                void handleDeleteScheme();
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default CourseGradingSchemeDialog;
